import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { List, Map } from 'immutable';
import { Link } from 'react-router-dom';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';
import classNames from 'classnames';

import AutoComplete from '../../../helpers/AutoComplete';
import FormSubmitOnBlur from '../../../helpers/FormSubmitOnBlur';
import UrlMaker from '../../../../routing/UrlMaker';
import Span from '../../../helpers/Span';
import { unwrap, isChanged } from '../../../../utils/ChangeSpy';
import uniqueId from '../../../../utils/uniqueId';
import BaseInput from '../../../helpers/Input';
import FormGroup from '../../../helpers/FormGroup';
import LoadAnimation from '../../../decorators/LoadAnimation';

import PrettyError from '../../../decorators/PrettyError';
import DisplayError from '../../../decorators/DisplayError';
import { Collapse } from '../../../helpers/Collapse';
import Button from '../../../helpers/Button';

import style from './style.module.css';

const Input = PrettyError(DisplayError(BaseInput));

const renderSuggestion = suggestion => <span>{suggestion.text}</span>;

const ContextMenuWrapper = onClick => {
  const ContextMenuWrapper = (value, data) => {
    const id = uniqueId();

    return (
      <div>
        <ContextMenuTrigger id={id}>{value}</ContextMenuTrigger>

        <ContextMenu id={id}>
          <MenuItem data={data.toJS()} onClick={onClick}>
            <i className="fa fa-remove" /> Delete
          </MenuItem>
        </ContextMenu>
      </div>
    );
  };

  return ContextMenuWrapper;
};

/**
 * Contact executive fund component.
 *
 * @param props {Object}.
 * @param props.fund {Immutable.Map} Fund definition.
 * @returns {XML}
 * @class
 */
const ContactExecutiveFund = props => {
  const { id = uniqueId(), onDelete, onClick, fund, onActivate, projectFieldName, suggestProject } = props;

  const isOpened = fund.get('isOpened');
  const currentStatus = fund.get('currentStatus');
  const highStatus = fund.get('highStatus');

  let headerContent = fund.get('fundLegalName');

  if (isOpened) {
    const companyId = fund.get('fundId');
    const url = UrlMaker.create('/company/:companyId/').mapParams({
      companyId,
    });

    headerContent = (
      <Link target="_blank" to={url}>
        {headerContent}
      </Link>
    );
  }

  const activeState = unwrap(fund.get('activeFund', false)) ? (
    'currently active'
  ) : (
    <a onClick={onActivate}>make active</a>
  );
  const stateClass = classNames({
    changed: isChanged(fund.get('activeFund')),
  });

  const header = ContextMenuWrapper(onDelete)(
    <div className="panel-heading" onClick={onClick}>
      <h4 className="panel-title"> {headerContent} </h4>
      <span className="pull-right">
        <b>
          <Span checkOn="children">{currentStatus}</Span>
        </b>
        <span> / </span>
        <Span checkOn="children">{highStatus}</Span>
      </span>
    </div>,
    fund,
  );

  return (
    <div className="panel panel-default">
      {header}
      <Collapse className={classNames('panel-collapse', { in: isOpened })} id={id} isOpen={isOpened}>
        <div className="panel-body">
          <div className="row">
            <div className="col-xs-6">
              <AutoComplete
                checkOn="text"
                formGroupClass="mb0"
                getSuggestion={params =>
                  suggestProject.onFetch({
                    ...params,
                    fundId: fund.get('fundId'),
                  })
                }
                getSuggestionValue={suggestProject.getValue}
                inputProps={{
                  className: 'form-control input-sm',
                  label: 'Project',
                  name: projectFieldName,
                }}
                onSuggestionSelected={(...rest) => suggestProject.onSelect(projectFieldName, ...rest)}
                onUpdateSuggestions={event => suggestProject.onUpdate(projectFieldName, event)}
                renderSuggestion={renderSuggestion}
                suggestions={suggestProject.suggests}
                text={fund.get('eprojectCategory', '')}
              />
            </div>
            <div className="col-xs-6">
              <FormGroup>
                <Input
                  className="input-sm"
                  label="Priority"
                  name="source"
                  placeholder="Priority"
                  value=""
                  readOnly
                  showLabel
                />
                {/* TODO check label */}
              </FormGroup>
            </div>
          </div>
          <div className="text-right">
            <i className={stateClass}> {activeState} </i>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

ContactExecutiveFund.propTypes = {
  fund: PropTypes.instanceOf(Map).isRequired,
  id: PropTypes.string,
  onActivate: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
};

/**
 * Contact executive funds' list component.
 *
 * @param props {Object}.
 * @param props.funds {Immutable.List} Fund definition.
 * @param props.onClick {Function} On fund's click callback.
 * @param props.addFund {Function} On add Fund click callback.
 */
const ContactExecutiveFunds = props => {
  const { funds, id = uniqueId(), onActivate, addFund, onDelete, onClick, onSubmit, suggestProject, onBlur } = props;

  const fundsContent = funds.map((fund, i) => (
    <ContactExecutiveFund
      key={fund.get('id')}
      fund={fund}
      onActivate={event => onActivate(event, i)}
      onClick={event => onClick(event, i)}
      onDelete={onDelete}
      parentId={id}
      projectFieldName={`funds.${i}.eprojectCategory`}
      suggestProject={suggestProject}
    />
  ));

  return (
    <div className={classNames(style.host, 'accordion-right-side mt10')} id={id}>
      <p className="mb0"> Funds </p>
      <FormSubmitOnBlur onComponentBlur={onBlur} onSubmit={onSubmit} focusOnClick>
        {fundsContent}
      </FormSubmitOnBlur>

      <div className="clearfix" />
      <Button className="btn-primary btn-block" onClick={addFund}>
        + Add new fund...
      </Button>
    </div>
  );
};

ContactExecutiveFunds.propTypes = {
  addFund: PropTypes.func.isRequired,
  funds: PropTypes.instanceOf(List).isRequired,
  id: PropTypes.string,
  onActivate: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default LoadAnimation(memo(ContactExecutiveFunds));
